import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCustomerCategories } from "@/hooks/useCustomerCategories";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
// UPPDRAG-30: Globe icon added for the new "Domäner" settings tab
// (consolidated from the separate /manager/domains sidebar entry).
import { Loader2, Save, Building2, CalendarClock, XCircle, Bell, FileText, Settings2, Package, Coins, Wallet, CreditCard, ToggleLeft, Tag, Webhook, Trophy, Search, Globe } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

import SettingsClubAndVenue from "./settings/SettingsClubAndVenue";
import SettingsBookingRules from "./settings/SettingsBookingRules";
import SettingsCancellation from "./settings/SettingsCancellation";
import SettingsStandingTimes from "./settings/SettingsStandingTimes";
import SettingsNotifications from "./settings/SettingsNotifications";
import SettingsTexts from "./settings/SettingsTexts";
import SettingsGeneral from "./settings/SettingsGeneral";
import AdminAddons from "./AdminAddons";
import AdminRewards from "./AdminRewards";
import AdminCredits from "./AdminCredits";
import AdminVouchers from "./AdminVouchers";
import SettingsBilling from "./settings/SettingsBilling";
import SettingsFeatures from "./settings/SettingsFeatures";
import SettingsWebhooks from "./settings/SettingsWebhooks";
import AdminReferralProgram from "./AdminReferralProgram";
import SettingsActivityTypes from "./settings/SettingsActivityTypes";
import SettingsLeagues from "./settings/SettingsLeagues";
// UPPDRAG-30: existing DomainsPage moved into AdminSettings as a tab.
// The page is self-contained (its render starts with `<div
// className="space-y-4">`, no h1/page chrome) so it drops in cleanly
// — no refactor needed.
import DomainsPage from "@/pages/manager/DomainsPage";
import { Gift, Zap, Shield } from "lucide-react";
import SettingsRoles from "./settings/SettingsRoles";

const sectionDefs = [
  { id: "club", labelKey: "settingsPage.sections.club", icon: Building2, keywords: ["klubb", "club", "namn", "name", "logotyp", "logo", "adress", "address", "kontakt", "contact", "email", "telefon", "phone", "hemsida", "website", "valuta", "currency", "tidzon", "timezone", "färg", "color", "branding", "ikon", "icon", "anläggning", "venue", "hall", "öppettider", "opening hours"] },
  { id: "features", labelKey: "settingsPage.sections.features", icon: ToggleLeft, keywords: ["funktioner", "features", "aktivera", "enable", "inaktivera", "disable", "toggle", "dörrkod", "door code", "kö", "queue", "väntelista", "waitlist", "serie", "league", "turnering", "tournament", "träning", "training"] },
  { id: "billing", labelKey: "settingsPage.sections.billing", icon: CreditCard, keywords: ["fakturering", "billing", "betalning", "payment", "stripe", "swish", "bankgiro", "kontant", "cash", "faktura", "invoice", "moms", "vat", "tax", "företag", "company", "organisationsnummer", "org number", "betalvillkor", "payment terms", "fortnox"] },
  { id: "booking", labelKey: "settingsPage.sections.booking", icon: CalendarClock, keywords: ["bokning", "booking", "bokningsregler", "booking rules", "max", "tid", "time", "förbokning", "advance booking", "kategori", "category", "medlem", "member", "standard", "court assignment", "bankategori", "court group", "bokningsläge", "booking mode"] },
  { id: "cancellation", labelKey: "settingsPage.sections.cancellation", icon: XCircle, keywords: ["avbokning", "cancellation", "avbokningsregler", "cancellation rules", "återbetalning", "refund", "deadline", "avgift", "fee"] },
  { id: "standing-times", labelKey: "settingsPage.sections.standingTimes", icon: CalendarClock, keywords: ["stående", "standing", "ledigställ", "release", "abonnemang", "subscription", "kredit", "credit", "giltighetstid", "validity", "återbetalning", "refund"] },
  { id: "leagues", labelKey: "settingsPage.sections.leagues", icon: Trophy, keywords: ["serie", "league", "serier", "leagues", "liga", "omgång", "round", "division", "poäng", "points", "matchdag", "match day"] },
  { id: "notifications", labelKey: "settingsPage.sections.notifications", icon: Bell, keywords: ["notifikationer", "notifications", "notiser", "påminnelse", "reminder", "email", "sms", "push", "meddelande", "message"] },
  { id: "texts", labelKey: "settingsPage.sections.texts", icon: FileText, keywords: ["texter", "texts", "meddelande", "message", "bokningsbekräftelse", "booking confirmation", "välkomsttext", "welcome", "regler", "rules", "villkor", "terms"] },
  { id: "general", labelKey: "settingsPage.sections.general", icon: Settings2, keywords: ["allmänt", "general", "språk", "language", "registrering", "registration", "signup", "anmälan", "offentlig", "public"] },
  { id: "addons", labelKey: "settingsPage.sections.addons", icon: Package, keywords: ["tillägg", "addons", "extra", "produkt", "product", "tillbehör", "accessories", "racket", "boll", "ball", "dryck", "drink"] },
  { id: "rewards", labelKey: "settingsPage.sections.rewards", icon: Coins, keywords: ["belöningar", "rewards", "poäng", "points", "bonus", "lojalitet", "loyalty"] },
  { id: "credits", labelKey: "settingsPage.sections.credits", icon: Wallet, keywords: ["klippkort", "punch card", "krediter", "credits", "saldo", "balance"] },
  { id: "vouchers", labelKey: "settingsPage.sections.vouchers", icon: Tag, keywords: ["presentkort", "gift card", "värdekort", "voucher", "kupong", "coupon", "rabatt", "discount", "rabattkod", "discount code"] },
  { id: "webhooks", labelKey: "settingsPage.sections.webhooks", icon: Webhook, keywords: ["webhook", "integration", "api", "koppling", "connection"] },
  // UPPDRAG-30: Domäner moved here from a top-level sidebar entry.
  // Sits alongside Webhooks & API since both deal with advanced
  // integration / custom-hostname configuration.
  { id: "domains", labelKey: "settingsPage.sections.domains", icon: Globe, keywords: ["domän", "domain", "hostname", "subdomän", "subdomain", "dns", "txt", "verifiera", "verify", "primary", "wildcard"] },
  { id: "activity-types", labelKey: "settingsPage.sections.activityTypes", icon: Zap, keywords: ["aktivitetstyper", "activity types", "öppen match", "open match", "padel", "typ", "type"] },
  { id: "referral", labelKey: "settingsPage.sections.referral", icon: Gift, keywords: ["referral", "värvning", "rekommendation", "recommendation", "bonus", "dela", "share"] },
  { id: "roles", labelKey: "settingsPage.sections.roles", icon: Shield, keywords: ["roller", "roles", "behörighet", "permissions", "ägare", "owner", "hallchef", "personal", "staff", "tränare", "coach", "rättigheter", "access"] },
] as const;

type SectionId = typeof sectionDefs[number]["id"];

// G-102: the global Save button persists ONLY org/hall fields fed through
// updateField/updateSetting/updateHall (saveMutation). Sections with their own
// mutations (features, addons, rewards, credits, vouchers, webhooks, domains,
// activity-types, referral, roles, billing) must not render it.
const SAVABLE_SECTIONS: ReadonlySet<SectionId> = new Set<SectionId>([
  "club", "booking", "cancellation", "standing-times", "leagues", "notifications", "texts", "general",
]);

export default function AdminSettings() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  // UPPDRAG-30: deep-link a specific tab via `?tab=…` (used by the
  // /manager/domains → /manager/settings?tab=domains redirect, and
  // any future sharable settings link). Falls back to "club" when
  // the param is missing or doesn't match a real section id.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSection = ((): SectionId => {
    const raw = searchParams.get("tab");
    if (raw && sectionDefs.some((s) => s.id === raw)) return raw as SectionId;
    return "club";
  })();
  const [activeSection, setActiveSection] = useState<SectionId>(initialSection);
  const [searchQuery, setSearchQuery] = useState("");

  // UPPDRAG-30: clear the `?tab=` query param after consuming it so a
  // subsequent click on a different sidebar item doesn't visually
  // conflict with stale URL state. setSearchParams with an empty
  // object preserves any other params and just drops `tab`.
  useEffect(() => {
    if (searchParams.get("tab")) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
    // Run once on mount; intentionally no deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [hallForm, setHallForm] = useState<Record<string, unknown>>({});

  const filteredSections = useMemo(() => {
    const sections = searchQuery.trim()
      ? sectionDefs.filter((s) => {
          const q = searchQuery.toLowerCase();
          const label = t(s.labelKey).toLowerCase();
          return label.includes(q) || s.keywords.some((kw) => kw.includes(q));
        })
      : [...sectionDefs];
    // Sort A–Ö with "club" always first
    return sections.sort((a, b) => {
      if (a.id === "club") return -1;
      if (b.id === "club") return 1;
      return t(a.labelKey).localeCompare(t(b.labelKey), "sv");
    });
  }, [searchQuery, t]);

  useEffect(() => {
    if (filteredSections.length > 0 && !filteredSections.some((s) => s.id === activeSection)) {
      setActiveSection(filteredSections[0].id);
    }
  }, [filteredSections, activeSection]);

  const { categories, updateCategoryRules } = useCustomerCategories(organization?.id);

  const { data: org, isLoading } = useQuery({
    queryKey: ["admin-org-settings", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", organization!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!organization?.id,
  });

  const { data: hall } = useQuery({
    queryKey: ["admin-hall", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("halls")
        .select("*")
        .eq("organization_id", organization!.id)
        .limit(1)
        .single();
      return data;
    },
    enabled: !!organization?.id,
  });

  const currentOrg = { ...org, ...form };
  const currentHall = hall ? { ...hall, ...hallForm } : null;

  const updateField = (field: string, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateSetting = (key: string, value: unknown) => {
    const merged = { ...((org?.settings as Record<string, unknown>) || {}), ...(form.settings || {}) };
    setForm((prev) => ({ ...prev, settings: { ...merged, [key]: value } }));
  };

  const updateHall = (field: string, value: unknown) => {
    setHallForm((prev) => ({ ...prev, [field]: value }));
  };

  const settings = { ...((org?.settings as Record<string, unknown>) || {}), ...(form.settings || {}) };

  const handleUpdateBookingRules = (catId: string, rules: Record<string, unknown> | null) => {
    updateCategoryRules.mutate({ id: catId, booking_rules: rules });
  };

  const handleUpdateCancellationRules = (catId: string, rules: Record<string, unknown> | null) => {
    updateCategoryRules.mutate({ id: catId, cancellation_rules: rules });
  };

  /** Persist a single field to the DB immediately (used for file uploads that shouldn't wait for Save click) */
  const persistField = async (field: string, value: unknown) => {
    if (!organization?.id) return;
    const { error } = await supabase
      .from("organizations")
      .update({ [field]: value } as Record<string, unknown>)
      .eq("id", organization.id);
    if (error) throw error;
    queryClient.invalidateQueries({ queryKey: ["admin-org-settings"] });
    queryClient.invalidateQueries({ queryKey: ["auth-org-branding"] });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!organization?.id) throw new Error("No org");

      const { error: orgError, count: _count } = await supabase
        .from("organizations")
        .update({
          name: currentOrg.name,
          slug: currentOrg.slug,
          logo_url: currentOrg.logo_url || null,
          app_icon_url: currentOrg.app_icon_url || null,
          email_logo_url: currentOrg.email_logo_url || null,
          brand_color: currentOrg.brand_color || null,
          primary_color: currentOrg.primary_color || null,
          secondary_color: currentOrg.secondary_color || null,
          contact_email: currentOrg.contact_email || null,
          contact_phone: currentOrg.contact_phone || null,
          website: currentOrg.website || null,
          address: currentOrg.address || null,
          default_currency: currentOrg.default_currency || "SEK",
          default_language: currentOrg.default_language || "sv",
          timezone: currentOrg.timezone || "Europe/Stockholm",
          settings: { ...((org?.settings as Record<string, unknown>) || {}), ...(form.settings || {}) },
          ...(currentOrg.max_slots_per_booking !== undefined ? { max_slots_per_booking: currentOrg.max_slots_per_booking } : {}),
          ...(currentOrg.max_concurrent_bookings !== undefined ? { max_concurrent_bookings: currentOrg.max_concurrent_bookings } : {}),
          ...(currentOrg.member_max_slots_per_booking !== undefined ? { member_max_slots_per_booking: currentOrg.member_max_slots_per_booking } : {}),
          ...(currentOrg.member_max_concurrent_bookings !== undefined ? { member_max_concurrent_bookings: currentOrg.member_max_concurrent_bookings } : {}),
        } as Record<string, unknown>)
        .eq("id", organization.id);

      if (orgError) throw orgError;

      if (hall && Object.keys(hallForm).length > 0) {
        const { error: hallError } = await supabase.from("halls").update(hallForm).eq("id", hall.id);
        if (hallError) throw hallError;
      }
    },
    onSuccess: () => {
      toast.success(t("settingsPage.settingsSaved"));
      queryClient.invalidateQueries({ queryKey: ["admin-org-settings"] });
      queryClient.invalidateQueries({ queryKey: ["admin-hall"] });
      queryClient.invalidateQueries({ queryKey: ["auth-org-branding"] });
      setForm({});
      setHallForm({});
    },
    onError: (err: Error) => toast.error(err.message || t("common.couldNotSave")),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!org) return <p className="text-muted-foreground">{t("common.noOrganization")}</p>;

  const renderSection = (id: SectionId) => {
    switch (id) {
      case "club": return <SettingsClubAndVenue org={currentOrg} updateField={updateField} persistField={persistField} hall={currentHall} updateHall={updateHall} />;
      case "features": return <SettingsFeatures />;
      case "billing": return <SettingsBilling />;
      case "booking": return <SettingsBookingRules settings={settings} updateSetting={updateSetting} hall={currentHall} updateHall={updateHall} categories={categories} onUpdateCategoryRules={handleUpdateBookingRules} org={currentOrg} updateOrgField={updateField} />;
      case "cancellation": return <SettingsCancellation settings={settings} updateSetting={updateSetting} hall={currentHall} updateHall={updateHall} categories={categories} onUpdateCategoryRules={handleUpdateCancellationRules} />;
      case "standing-times": return <SettingsStandingTimes settings={settings} updateSetting={updateSetting} />;
      case "leagues": return <SettingsLeagues settings={settings} updateSetting={updateSetting} />;
      case "notifications": return <SettingsNotifications settings={settings} updateSetting={updateSetting} />;

      case "texts": return <SettingsTexts settings={settings} updateSetting={updateSetting} />;
      case "general": return <SettingsGeneral org={currentOrg} updateField={updateField} />;
      case "addons": return <AdminAddons />;
      case "rewards": return <AdminRewards />;
      case "credits": return <AdminCredits />;
      case "vouchers": return <AdminVouchers />;
      case "webhooks": return <SettingsWebhooks />;
      // UPPDRAG-30: Domäner tab — mounts the existing DomainsPage as-is.
      case "domains": return <DomainsPage />;
      case "activity-types": return <SettingsActivityTypes />;
      case "referral": return <AdminReferralProgram />;
      case "roles": return <SettingsRoles />;
    }
  };

  if (isMobile) {
    return (
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={t("settingsPage.searchPlaceholder")} className="h-9 text-sm pl-8" />
        </div>
        {filteredSections.length === 0 ? (
          <p className="text-sm text-muted-foreground px-3 py-4">{t("settingsPage.noMatch", { query: searchQuery })}</p>
        ) : (
        <Accordion type="single" collapsible className="space-y-2">
          {filteredSections.map(({ id, labelKey, icon: Icon }) => (
            <AccordionItem key={id} value={id} className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  {t(labelKey)}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4">
                {renderSection(id)}
                {/* G-102: Save renders only under sections saveMutation actually persists */}
                {SAVABLE_SECTIONS.has(id) && (
                  <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} className="w-full mt-4">
                    {saveMutation.isPending ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
                    {t("settingsPage.saveSettings")}
                  </Button>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        )}
      </div>
    );
  }

  return (
    <div className="flex gap-6 min-h-[500px]">
      <nav className="w-52 shrink-0 space-y-1">
        <div className="mb-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={t("settingsPage.searchPlaceholder")} className="h-9 text-sm pl-8" />
          </div>
        </div>
        {filteredSections.length === 0 ? (
          <p className="text-sm text-muted-foreground px-3 py-4">{t("settingsPage.noMatch", { query: searchQuery })}</p>
        ) : filteredSections.map(({ id, labelKey, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveSection(id)}
            className={cn(
              "w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors text-left",
              activeSection === id
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {t(labelKey)}
          </button>
        ))}
        </nav>

      <div className="flex-1 min-w-0">
        <div className="rounded-lg border bg-card p-6">
          {renderSection(activeSection)}
        </div>
        {/* G-102: Save renders only under sections saveMutation actually persists */}
        {SAVABLE_SECTIONS.has(activeSection) && (
          <div className="mt-4">
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
              {t("settingsPage.saveSettings")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
